import { Handle } from "reactflow";
import styled, { css } from "styled-components";
import themeGet from "@styled-system/theme-get";
import { Badge, Button, Card } from "orcs-design-system";

export const EDGE_COLOR = "#b8bbbd";

export const DEFAULT_CARD_THEME = {
  color: "greyLight",
  light: "grey",
  alt: "greyLight",
  animationVariant: "primary",
};
export const DIFF_STATE_THEMES = {
  added: {
    color: "success",
    light: "successLighter",
    alt: "successLighter",
    bgColor: "success10",
  },
  new: {
    color: "success",
    light: "successLighter",
    alt: "successLighter",
    bgColor: "success10",
  },
  removed: {
    ...DEFAULT_CARD_THEME,
    bgColor: "greyLightest",
  },
  updated: {
    ...DEFAULT_CARD_THEME,
    color: "primary",
  },
  movedIn: {
    color: "warning",
    light: "warningLighter",
    alt: "warningLighter",
    bgColor: "warning10",
  },
  movedOut: {
    color: "warning",
    light: "warningLighter",
    alt: "warningLighter",
    bgColor: "warning10",
  },
};

export const changeTypes = {
  ADDED: "added",
  MOVED_IN: "movedIn",
  MOVED_OUT: "movedOut",
  REMOVED: "removed",
  NEW: "new",
};

export const changes = {
  [changeTypes.ADDED]: "Added",
  [changeTypes.MOVED_IN]: "Moved in",
  [changeTypes.MOVED_OUT]: "Moved out",
  [changeTypes.REMOVED]: "Removed",
  [changeTypes.NEW]: "New",
};

export const getVisibilityStyles = ({ $hidden, xPos, yPos }) => {
  const hidden = $hidden || (xPos === 0 && yPos === 0);

  return css`
    pointer-events: ${hidden ? "none" : "auto"} !important;
    opacity: ${hidden ? 0 : 1};
  `;
};

export const HIGHLIGHTED_ANIMATION = {
  DURATION: 700,
  REPETITIONS: 4,
};

export const StyledCard = styled(Card)((props) => {
  const {
    $theme = DEFAULT_CARD_THEME,
    active,
    width = "460px",
    minWidth = "300px",
    maxWidth = "500px",
    cursor = "pointer",
  } = props;
  const { animationVariant = DEFAULT_CARD_THEME.animationVariant } =
    $theme || {};
  const animationTiming = `${HIGHLIGHTED_ANIMATION.DURATION}ms ${HIGHLIGHTED_ANIMATION.REPETITIONS} linear`;
  const bgColor = $theme.bgColor
    ? themeGet(`colors.${$theme.bgColor}`)
    : "var(--bg-color)";

  return css`
    @property --rotate {
      syntax: "<angle>";
      initial-value: 132deg;
      inherits: false;
    }

    --border-size: 2px;
    --bg-color: #ffffff;
    --rotate: 0deg;
    position: relative;
    width: ${width};
    min-width: ${minWidth};
    max-width: ${maxWidth};
    cursor: ${cursor};
    border: solid ${active ? "2px" : "var(--border-size)"}
      ${themeGet(`colors.${active ? $theme?.light : $theme?.alt}`)};
    background: ${bgColor};
    border-radius: ${themeGet("radii.2")} !important;
    box-shadow: 0px 7px 14px rgba(0, 0, 0, 0.1);
    ${getVisibilityStyles(props)}
    z-index: 2;

    transition: all 1s;

    &.highlighted {
      border-width: 2px;
      overflow: hidden;
      animation: pulse ${animationTiming};
    }

    &.highlighted::before {
      content: "";
      width: 120%;
      aspect-ratio: 1 / 1;
      border-radius: ${themeGet("radii.2")};
      background-image: linear-gradient(
        var(--rotate),
        ${themeGet(`colors.${animationVariant}Lighter`)},
        ${themeGet(`colors.${animationVariant}Lightest`)},
        ${themeGet(`colors.white`)}
      );
      position: absolute;
      z-index: -1;
      top: -160%;
      left: -10%;
      animation: spin ${animationTiming};
    }

    &.highlighted::after {
      --offsetSize: calc(var(--border-size) + 1px);
      --size: calc(100% - var(--offsetSize) - var(--offsetSize));
      content: "";
      width: var(--size);
      height: var(--size);
      border-radius: ${themeGet("radii.2")};
      background: var(--bg-color);
      position: absolute;
      z-index: -1;
      top: var(--offsetSize);
      left: var(--offsetSize);
      animation: spin ${animationTiming};
    }

    @keyframes pulse {
      0% {
        transform: scale(1);
      }

      50% {
        transform: scale(1.04);
      }

      100% {
        transform: scale(1);
      }
    }

    @keyframes spin {
      0% {
        --rotate: 0deg;
      }
      100% {
        --rotate: 360deg;
      }
    }
  `;
});

export const StyledBadge = styled(Badge)`
  display: flex;
  min-width: 36px;
  height: 36px;
  justify-content: center;
  align-items: center;
  margin-top: 2px;
  background: ${(props) => themeGet(`colors.${props.color}`)};
`;

const getHandleStyle = ({ $hidden, $yPosition }) => {
  if ($hidden) {
    return css`
      background: transparent;
      border: none;
    `;
  }

  return css`
    ${$yPosition && `top: ${$yPosition};`}

    &.triangle {
      width: 0px;
      height: 0px;
      border-radius: 0;
      background: transparent;
    }
  `;
};

export const TargetHandle = styled(Handle)(({ $hidden, $yPosition }) => {
  return css`
    &.react-flow__handle {
      ${getHandleStyle({ $hidden, $yPosition })}

      &.triangle {
        left: -20px;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
        border-right: 18px solid ${EDGE_COLOR};
      }
    }
  `;
});

export const SourceHandle = styled(Handle)(({ $hidden, $yPosition }) => {
  return css`
    &.react-flow__handle {
      ${getHandleStyle({ $hidden, $yPosition })}

      &.triangle {
        right: -20px;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
        border-left: 18px solid ${EDGE_COLOR};
      }
    }
  `;
});

export const StyledSimpleButton = styled(Button)`
  background: ${themeGet("colors.white")};
  border: 1px solid ${themeGet("colors.greyLighter")};
  color: ${themeGet("colors.greyDarker")};
  gap: ${themeGet("space.s")};
  &:hover:not(:disabled) {
    background: ${themeGet("colors.primaryLightest")};
    color: ${themeGet("colors.greyDarker")};
    border: 1px solid ${themeGet("colors.greyLighter")};
  }
  &:hover:disabled {
    background: white;
    color: ${themeGet("colors.greyDarker")};
    border: 1px solid ${themeGet("colors.greyLighter")};
    cursor: pointer;
  }
`;

export const StyledSimpleTotals = styled.div`
  background: ${themeGet("colors.white")};
  border: 1px solid ${themeGet("colors.greyLighter")};
  color: ${themeGet("colors.greyDarker")};
  display: flex;
  font-size: ${themeGet("fontSizes.2")};
  font-weight: ${themeGet("fontWeights.2")};
  align-items: center;
  gap: ${themeGet("space.s")};
  padding: ${themeGet("space.xs")} ${themeGet("space.s")};
  border-radius: ${themeGet("radii.2")};
`;
